/**
 * @file frontend/src/lib/offline_queue.js
 * @description IndexedDB-backed offline queue for service log mutations (WP-1.1).
 *
 * The Service Worker (`/sw.js`) handles GET caching. Mutations go through
 * THIS module because we need:
 *   - Idempotency (retry-safe via client-side UUID).
 *   - User-visible "pending" state (flush count, last sync time, errors).
 *   - Per-record retry/back-off without losing the entry.
 *
 * Storage shape:
 *   IndexedDB database 'k12erp-offline'
 *     store 'service_logs'
 *       key: client_uuid (string)
 *       value: {
 *         client_uuid, payload (the POST body),
 *         created_at (ISO), status ('pending'|'syncing'|'synced'|'error'),
 *         attempts, last_error, last_attempt_at
 *       }
 *
 * Public API:
 *   - enqueueServiceLog(payload) → promise<entry>
 *   - listEntries() → promise<entry[]>
 *   - flushQueue(api) → promise<{synced, failed, total}>
 *   - clearSynced() → promise<count>
 *   - subscribe(listener) → unsubscribe (fires on every queue change)
 */

import { SERVICE_LOG_STORE, openOfflineDb } from './offlineDb';

const STORE = SERVICE_LOG_STORE;

const _listeners = new Set();

function _notify() {
  _listeners.forEach((fn) => {
    try { fn(); } catch (e) { /* ignore listener errors */ }
  });
}

function openDb() {
  return openOfflineDb();
}

function _uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (older Safari).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export async function enqueueServiceLog(payload) {
  const db = await openDb();
  const entry = {
    client_uuid: payload.client_uuid || _uuid(),
    payload: { ...payload, client_uuid: payload.client_uuid || _uuid() },
    created_at: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
    last_error: null,
    last_attempt_at: null,
  };
  // Make sure payload carries the same uuid as the entry key.
  entry.payload.client_uuid = entry.client_uuid;
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  _notify();
  return entry;
}

export async function listEntries() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _put(entry) {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Replay pending entries against the backend. Caller passes the axios
 * `api` instance from `lib/api.js`. Each entry attempts up to 5 times
 * across calls; entries that 4xx (validation failures from the server)
 * stay as 'error' until manually retried.
 *
 * Exponential backoff: pending entries that already failed will not be
 * re-attempted until `next_retry_at` has elapsed. Backoff doubles each
 * attempt (15s, 30s, 60s, 120s, 240s) capped at 5 minutes.
 */
function _nextBackoffMs(attempts) {
  // Cap at 5 minutes (300s). Pattern: 15s, 30s, 60s, 120s, 240s.
  return Math.min(15_000 * 2 ** Math.max(0, attempts - 1), 300_000);
}

export async function flushQueue(api) {
  const all = await listEntries();
  const now = Date.now();
  const pending = all.filter((e) => {
    if (e.status !== 'pending' && e.status !== 'syncing') return false;
    if (e.next_retry_at && new Date(e.next_retry_at).getTime() > now) return false;
    return true;
  });
  let synced = 0;
  let failed = 0;
  for (const entry of pending) {
    if (entry.attempts >= 5) {
      failed += 1;
      continue;
    }
    entry.status = 'syncing';
    entry.attempts += 1;
    entry.last_attempt_at = new Date().toISOString();
    await _put(entry);
    try {
      // Server is expected to honor the client_uuid as an idempotency key.
      // The /pwa/service-log-drafts/ endpoint upserts on client_uuid so
      // a duplicate POST after a flaky network is a no-op (200 instead
      // of 201) — exactly what we want for offline replay.
      await api.post('/medicaid/pwa/service-log-drafts/', entry.payload);
      entry.status = 'synced';
      entry.last_error = null;
      entry.next_retry_at = null;
      synced += 1;
    } catch (err) {
      const isClientError = err?.response?.status >= 400 && err.response.status < 500;
      entry.status = isClientError ? 'error' : 'pending';
      entry.last_error = err?.response?.data?.detail
        || err?.message || 'unknown sync error';
      // Schedule the next retry with exponential backoff.
      entry.next_retry_at = isClientError ? null
        : new Date(Date.now() + _nextBackoffMs(entry.attempts)).toISOString();
      failed += 1;
    }
    await _put(entry);
    _notify();
  }
  return { synced, failed, total: pending.length };
}

export async function clearSynced() {
  const all = await listEntries();
  const synced = all.filter((e) => e.status === 'synced');
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    synced.forEach((e) => store.delete(e.client_uuid));
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  _notify();
  return synced.length;
}

export async function retryEntry(client_uuid) {
  const all = await listEntries();
  const e = all.find((x) => x.client_uuid === client_uuid);
  if (!e) return null;
  e.status = 'pending';
  e.attempts = 0;
  e.last_error = null;
  await _put(e);
  _notify();
  return e;
}

export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/** Best-effort attempt to register a Background Sync. */
export async function requestBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('service-log-replay');
      return true;
    } catch { return false; }
  }
  return false;
}

/**
 * Returns the current StorageManager estimate as {usage_pct, usage_mb, quota_mb}.
 * Older Androids report <50MB available; warn at >=80% usage.
 *
 * Falls back to {usage_pct: 0} when navigator.storage isn't available
 * (older Safari < 17, some embedded browsers).
 */
export async function estimateStorage() {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return { usage_pct: 0, usage_mb: 0, quota_mb: 0, supported: false };
  }
  try {
    const est = await navigator.storage.estimate();
    const usage = est.usage || 0;
    const quota = est.quota || 1;
    return {
      usage_pct: Math.round((usage / quota) * 100),
      usage_mb: Math.round(usage / (1024 * 1024)),
      quota_mb: Math.round(quota / (1024 * 1024)),
      supported: true,
    };
  } catch {
    return { usage_pct: 0, usage_mb: 0, quota_mb: 0, supported: false };
  }
}
